import * as React from 'react'
import { ChevronLeftIcon, ChevronRightIcon, MoreIcon } from '@/src/icons'

import { cn } from '@/registry/reapit/lib/utils'
import { buttonVariants, type Button } from '@/registry/reapit/ui/button'

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  )
}

function PaginationContent({ className, ...props }: React.ComponentProps<'ul'>) {
  return <ul data-slot="pagination-content" className={cn('flex flex-row items-center gap-1', className)} {...props} />
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>) {
  return <li data-slot="pagination-item" {...props} />
}

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<React.ComponentProps<typeof Button>, 'size'> &
  React.ComponentProps<'a'>

function PaginationLink({ className, isActive, size = 'icon', ...props }: PaginationLinkProps) {
  return (
    <a
      aria-current={isActive ? 'page' : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={cn(
        buttonVariants({
          variant: isActive ? 'outline' : 'tertiary',
          size,
        }),
        className,
      )}
      {...props}
    />
  )
}

function PaginationPrevious({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="medium"
      className={cn('gap-1.5 px-2.5 sm:pl-2.5', className)}
      {...props}
    >
      {/* DS: icon-md (20px) for navigation controls per Principal policy */}
      <ChevronLeftIcon className="size-5" />
      <span className="hidden sm:block">Previous</span>
    </PaginationLink>
  )
}

function PaginationNext({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="medium"
      className={cn('gap-1.5 px-2.5 sm:pr-2.5', className)}
      {...props}
    >
      <span className="hidden sm:block">Next</span>
      {/* DS: icon-md (20px) for navigation controls per Principal policy */}
      <ChevronRightIcon className="size-5" />
    </PaginationLink>
  )
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      {/* DS: inline dense icon — icon-sm (16px) */}
      <MoreIcon size="sm" />
      <span className="sr-only">More pages</span>
    </span>
  )
}

type SimplePaginationProps = React.ComponentProps<'nav'> & {
  pageNumber: number
  pageCount: number
  onPrevious?: () => void
  onNext?: () => void
}

function SimplePagination({ className, pageNumber, pageCount, onPrevious, onNext, ...props }: SimplePaginationProps) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="simple-pagination"
      className={cn('flex items-center gap-2', className)}
      {...props}
    >
      <button
        type="button"
        data-slot="simple-pagination-prev"
        onClick={onPrevious}
        disabled={pageNumber <= 1}
        aria-label="Go to previous page"
        className={cn(
          buttonVariants({ variant: 'tertiary', size: 'icon' }),
          'disabled:pointer-events-none disabled:opacity-50',
        )}
      >
        {/* DS: icon-md (20px) for navigation controls per Principal policy */}
        <ChevronLeftIcon className="size-5" />
      </button>
      <span
        data-slot="simple-pagination-count"
        className="text-sm text-muted-foreground tabular-nums"
        aria-live="polite"
        aria-atomic
      >
        {pageNumber} of {pageCount}
      </span>
      <button
        type="button"
        data-slot="simple-pagination-next"
        onClick={onNext}
        disabled={pageNumber >= pageCount}
        aria-label="Go to next page"
        className={cn(
          buttonVariants({ variant: 'tertiary', size: 'icon' }),
          'disabled:pointer-events-none disabled:opacity-50',
        )}
      >
        {/* DS: icon-md (20px) for navigation controls per Principal policy */}
        <ChevronRightIcon className="size-5" />
      </button>
    </nav>
  )
}

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
  SimplePagination,
}
